import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, CalendarDays, Ticket, Users, Clock, CheckCircle2, MapPin, Building2, ShoppingBag, Bell, BedDouble, UtensilsCrossed, Sliders } from "lucide-react";
import { toast } from "sonner";
import { DashboardShell, StatCard } from "../components/DashboardShell";
import { eur } from "../lib/format";
import { useAuth } from "../auth";
import { ProfileTab, ProductsTab } from "./operatorTabs";
import { OperatorExperiencesTab } from "./operatorExperiences";
import { OperatorActivitiesTab } from "./operatorActivities";
import { OperatorAccommodations } from "./accommodationTabs";
import { RestaurantTab } from "./restaurantTabs";
import * as apiClient from "../api";

const TABS = [
  { key: "today", label: "Oggi & Domani", icon: LayoutDashboard },
  { key: "notifications", label: "Notifiche", icon: Bell },
  { key: "bookings", label: "Prenotazioni", icon: Ticket },
  { key: "availability", label: "Disponibilità", icon: CalendarDays },
  { key: "experiences", label: "Esperienze", icon: Sliders },
  { key: "activities", label: "Attività & Servizi", icon: CalendarDays },
  { key: "accommodations", label: "Alloggi", icon: BedDouble },
  { key: "restaurant", label: "Ristorante & Menu", icon: UtensilsCrossed },
  { key: "products", label: "I Miei Prodotti", icon: ShoppingBag },
  { key: "profile", label: "Profilo Attività", icon: Building2 },
];

function NotificationsTab() {
  const qc = useQueryClient();
  const { data: notifs = [] } = useQuery({ queryKey: ["opNotifs"], queryFn: apiClient.operatorNotifications });
  const read = useMutation({ mutationFn: apiClient.readNotification, onSuccess: () => qc.invalidateQueries({ queryKey: ["opNotifs"] }) });
  return (
    <div className="space-y-2 max-w-2xl" data-testid="operator-notifications">
      {notifs.length === 0 ? <p className="text-sm text-slate-400">Nessuna notifica.</p> : notifs.map((n) => (
        <div key={n.id} className={`rounded-xl border p-4 flex items-start justify-between gap-3 ${n.read ? "border-slate-100 bg-white" : "border-secondary/40 bg-secondary/5"}`} data-testid={`notif-${n.id}`}>
          <div>
            <div className="font-semibold text-slate-800 text-sm">{n.title} · <span className="text-primary">{n.booking_number}</span></div>
            <div className="text-xs text-slate-500 mt-0.5">{n.body}</div>
          </div>
          {!n.read && <button onClick={() => read.mutate(n.id)} className="text-xs font-semibold text-secondary whitespace-nowrap">Segna letta</button>}
        </div>
      ))}
    </div>
  );
}

function Today() {
  const qc = useQueryClient();
  const { data: stats } = useQuery({ queryKey: ["opStats"], queryFn: apiClient.operatorStats });
  const { data: todayB = [] } = useQuery({ queryKey: ["opBookings", "today"], queryFn: () => apiClient.operatorBookings("today") });
  const { data: tomorrowB = [] } = useQuery({ queryKey: ["opBookings", "tomorrow"], queryFn: () => apiClient.operatorBookings("tomorrow") });
  const arrive = useMutation({ mutationFn: apiClient.markArrived, onSuccess: () => { qc.invalidateQueries(); toast.success("Cliente segnato come arrivato"); } });

  const BookingRow = ({ b }) => (
    <div className="flex items-center gap-3 rounded-xl border border-slate-100 p-3 bg-white" data-testid={`op-booking-${b.booking_number}`}>
      <img src={b.items[0]?.image} alt="" className="h-12 w-14 rounded-lg object-cover" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800 line-clamp-1">{b.items[0]?.title}</div>
        <div className="text-xs text-slate-400 flex gap-3">
          <span>{b.customer.name} {b.customer.surname}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{b.items[0]?.time}</span>
          <span className="flex items-center gap-1"><Users className="h-3 w-3" />{b.items[0]?.participants}</span>
        </div>
      </div>
      <button onClick={() => arrive.mutate(b.booking_number)} disabled={b.arrived} className={`rounded-full px-3 py-1.5 text-xs font-semibold flex items-center gap-1 ${b.arrived ? "bg-emerald-50 text-emerald-600" : "bg-primary text-white hover:bg-primary/90"}`}>
        <CheckCircle2 className="h-3.5 w-3.5" /> {b.arrived ? "Arrivato" : "Segna arrivo"}
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Oggi" value={stats?.today ?? 0} accent sub="prenotazioni" />
        <StatCard label="Domani" value={stats?.tomorrow ?? 0} sub="prenotazioni" />
        <StatCard label="Ricavi (netti)" value={eur(stats?.revenue)} accent />
        <StatCard label="GMV generato" value={eur(stats?.gmv)} />
        <StatCard label="Partecipanti" value={stats?.participants ?? 0} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Oggi</h3>
          <div className="space-y-2">{todayB.length === 0 ? <p className="text-sm text-slate-400">Nessuna prenotazione oggi.</p> : todayB.map((b) => <BookingRow key={b.id} b={b} />)}</div>
        </div>
        <div>
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Domani</h3>
          <div className="space-y-2">{tomorrowB.length === 0 ? <p className="text-sm text-slate-400">Nessuna prenotazione domani.</p> : tomorrowB.map((b) => <BookingRow key={b.id} b={b} />)}</div>
        </div>
      </div>
    </div>
  );
}

function BookingsTab() {
  const { data: bookings = [] } = useQuery({ queryKey: ["opBookings", "all"], queryFn: () => apiClient.operatorBookings() });
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-left"><tr>{["Numero", "Cliente", "Esperienza", "Data", "Ora", "Partecipanti", "Arrivo"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">
          {bookings.map((b) => (
            <tr key={b.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-semibold text-primary">{b.booking_number}</td>
              <td className="px-4 py-3">{b.customer.name} {b.customer.surname}</td>
              <td className="px-4 py-3 max-w-[180px] truncate">{b.items[0]?.title}</td>
              <td className="px-4 py-3 whitespace-nowrap">{b.items[0]?.date}</td>
              <td className="px-4 py-3">{b.items[0]?.time}</td>
              <td className="px-4 py-3">{b.items[0]?.participants}</td>
              <td className="px-4 py-3">{b.arrived ? <span className="text-emerald-600 text-xs font-semibold">✓ Arrivato</span> : <span className="text-slate-400 text-xs">In attesa</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AvailabilityTab() {
  const qc = useQueryClient();
  const [date, setDate] = useState("");
  const { data: avail = [] } = useQuery({ queryKey: ["opAvail", date], queryFn: () => apiClient.operatorAvailability(date || undefined) });
  const update = useMutation({ mutationFn: apiClient.updateAvailability, onSuccess: () => { qc.invalidateQueries({ queryKey: ["opAvail"] }); toast.success("Disponibilità aggiornata"); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore") });
  const cancelDep = useMutation({ mutationFn: apiClient.cancelDeparture, onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["opAvail"] }); toast.success(r.mode === "cancel" ? `Partenza annullata (${r.bookings_affected} prenotazioni in revisione)` : "Vendite chiuse"); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore") });

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-slate-600">Filtra per data:</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        {date && <button onClick={() => setDate("")} className="text-sm text-primary">Reset</button>}
      </div>
      <div className="space-y-6">
        {avail.map((row) => (
          <div key={row.experience.id} className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-heading font-semibold text-slate-900 mb-3">{row.experience.title}</h3>
            <div className="space-y-3">
              {row.availability.slice(0, date ? 1 : 5).map((a) => (
                <div key={a.date} className="border-t border-slate-50 pt-3">
                  <div className="text-xs font-semibold text-slate-400 mb-2">{a.date}</div>
                  <div className="flex flex-wrap gap-2">
                    {a.slots.map((s) => {
                      const closed = s.capacity === 0;
                      const free = Math.max(0, s.capacity - s.booked);
                      return (
                        <div key={s.time} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-1.5 flex-wrap" data-testid={`op-slot-${a.date}-${s.time}`}>
                          <span className="text-sm font-semibold flex items-center gap-1"><Clock className="h-3 w-3 text-primary" />{s.time}</span>
                          <span className="text-xs text-slate-500">Cap. <b>{s.capacity}</b> · Pren. <b>{s.booked}</b> · Disp. <b className="text-emerald-600">{free}</b></span>
                          <input type="number" min={s.booked} defaultValue={s.capacity} data-testid={`op-capacity-${a.date}-${s.time}`}
                            className="w-16 rounded border border-slate-200 px-2 py-1 text-xs"
                            onBlur={(e) => { const v = parseInt(e.target.value); if (v !== s.capacity) update.mutate({ experience_id: row.experience.id, date: a.date, time: s.time, capacity: v }); }} />
                          <button data-testid={`op-toggle-${a.date}-${s.time}`} onClick={() => update.mutate({ experience_id: row.experience.id, date: a.date, time: s.time, closed: !closed })}
                            className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${closed ? "bg-red-50 text-red-500" : "bg-emerald-50 text-emerald-600"}`}>
                            {closed ? "Chiuso" : "Aperto"}
                          </button>
                          <button data-testid={`op-cancel-dep-${a.date}-${s.time}`}
                            onClick={() => {
                              if (s.booked > 0) {
                                const reason = window.prompt(`ATTENZIONE: questa partenza ha ${s.booked} partecipanti prenotati.\nAnnullare NON cancella i pagamenti (revisione Super Admin).\nMotivo dell'annullamento?`);
                                if (reason) cancelDep.mutate({ experience_id: row.experience.id, date: a.date, time: s.time, mode: "cancel", reason });
                              } else {
                                cancelDep.mutate({ experience_id: row.experience.id, date: a.date, time: s.time, mode: "close_sales", reason: "" });
                              }
                            }}
                            className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-500">
                            {s.booked > 0 ? "Annulla partenza" : "Chiudi vendite"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Operator() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || "today";
  const tab = TABS.some((t) => t.key === rawTab) ? rawTab : "today";
  const setTab = (key) => setSearchParams(key === "today" ? {} : { tab: key });
  const { user } = useAuth();
  const { data: prof } = useQuery({ queryKey: ["operatorProfile"], queryFn: apiClient.getOperatorProfile });
  const opStatus = prof?.operator?.status;
  const titles = Object.fromEntries(TABS.map((t) => [t.key, t.label]));
  const banner = opStatus && opStatus !== "ACTIVE" ? (
    <div data-testid="operator-status-banner" className={`mb-5 rounded-xl border px-4 py-3 text-sm ${opStatus === "REJECTED" ? "border-red-200 bg-red-50 text-red-600" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
      {opStatus === "PENDING" && "Registrazione completata. Il tuo profilo è in fase di verifica da parte di Cristalda Experience. Riceverai una comunicazione quando sarà approvato."}
      {opStatus === "PENDING_CORRECTION" && `Sono state richieste modifiche al tuo profilo${prof?.operator?.correction_notes ? ": " + prof.operator.correction_notes : "."} Aggiorna i dati nella scheda Profilo Attività.`}
      {opStatus === "REJECTED" && `La tua richiesta non è stata approvata${prof?.operator?.rejection_reason ? ": " + prof.operator.rejection_reason : "."}`}
      {opStatus === "SUSPENDED" && "Il tuo account operatore è sospeso. Contatta Cristalda Experience."}
      {["INVITED", "IN_VERIFICA"].includes(opStatus) && "Profilo in fase di verifica."}
    </div>
  ) : null;
  return (
    <DashboardShell title={`Operatore · ${titles[tab]}`} tabs={TABS} active={tab} onTab={setTab}>
      {banner}
      {tab === "today" && <Today />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "bookings" && <BookingsTab />}
      {tab === "availability" && <AvailabilityTab />}
      {tab === "experiences" && <OperatorExperiencesTab />}
      {tab === "activities" && <OperatorActivitiesTab />}
      {tab === "accommodations" && <OperatorAccommodations />}
      {tab === "restaurant" && <RestaurantTab />}
      {tab === "products" && <ProductsTab operatorId={user?.operator_id} />}
      {tab === "profile" && <ProfileTab />}
    </DashboardShell>
  );
}
